import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { MindmapService, Mindmap as MindmapType } from '../../services/mindmapService';
import PageHeader from '../common/PageHeader';
import './Mindmap.css';

interface MindmapProps {
  onToggleSidebar: () => void;
}

const Mindmap: React.FC<MindmapProps> = ({ onToggleSidebar }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [mindmaps, setMindmaps] = useState<MindmapType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newMindmapTitle, setNewMindmapTitle] = useState('');
  const [newMindmapDescription, setNewMindmapDescription] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredMindmaps, setFilteredMindmaps] = useState<MindmapType[]>([]);

  useEffect(() => {
    if (user) {
      loadMindmaps();
    }
  }, [user]);

  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredMindmaps(mindmaps);
    } else {
      const filtered = mindmaps.filter(mindmap =>
        mindmap.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (mindmap.description && mindmap.description.toLowerCase().includes(searchQuery.toLowerCase()))
      );
      setFilteredMindmaps(filtered);
    }
  }, [mindmaps, searchQuery]);

  const loadMindmaps = async () => {
    if (!user) return;

    try {
      setLoading(true);
      setError(null);
      const response = await MindmapService.getUserMindmaps(user.id, 100, 0);
      setMindmaps(response.data);
    } catch (err) {
      console.error('Error loading mindmaps:', err);
      setError('Failed to load mindmaps');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateMindmap = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !newMindmapTitle.trim()) return;

    try {
      setError(null);
      const newMindmap = await MindmapService.createMindmap({
        user_id: user.id,
        title: newMindmapTitle.trim(),
        description: newMindmapDescription.trim() || undefined
      });
      setMindmaps([newMindmap, ...mindmaps]);
      setNewMindmapTitle('');
      setNewMindmapDescription('');
      setShowCreateForm(false);
    } catch (err) {
      console.error('Error creating mindmap:', err);
      setError('Failed to create mindmap');
    }
  };

  const handleDeleteMindmap = async (mindmapId: number) => {
    if (!window.confirm('Are you sure you want to delete this mindmap?')) return;

    try {
      setError(null);
      await MindmapService.deleteMindmap(mindmapId);
      setMindmaps(mindmaps.filter(mindmap => mindmap.id !== mindmapId));
    } catch (err) {
      console.error('Error deleting mindmap:', err);
      setError('Failed to delete mindmap');
    }
  };

  const handleMindmapClick = (mindmapId: number) => {
    navigate(`/mindmap/${mindmapId}`);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!user) {
    return (
      <div className="mindmap-container">
        <PageHeader
          onToggleSidebar={onToggleSidebar}
          centerContent={<h1 className="page-header-center-title">/mindmap</h1>}
        />
        <div className="mindmap-empty-state">
          <p>Please sign in to access mindmaps</p>
        </div>
      </div>
    );
  }

  return (
    <div className="mindmap-container">
      <PageHeader
        onToggleSidebar={onToggleSidebar}
        centerContent={<h1 className="page-header-center-title">/mindmap</h1>}
        rightActions={
          <button 
            className="mindmap-create-btn"
            onClick={() => setShowCreateForm(!showCreateForm)}
          >
            {showCreateForm ? '✕' : '+'} {showCreateForm ? 'Cancel' : 'New Mindmap'}
          </button>
        }
      />

      {error && (
        <div className="mindmap-error">
          {error}
          <button onClick={loadMindmaps} className="mindmap-retry-btn">Retry</button>
        </div>
      )}

      {showCreateForm && (
        <div className="mindmap-create-form">
          <form onSubmit={handleCreateMindmap}>
            <div className="create-form-header">
              <span className="create-form-title">Create New Mindmap</span>
            </div>
            <div className="create-form-content">
              <input
                type="text"
                placeholder="Enter mindmap title..."
                value={newMindmapTitle}
                onChange={(e) => setNewMindmapTitle(e.target.value)}
                className="create-mindmap-input"
                autoFocus
              />
              <textarea
                placeholder="Enter description (optional)..."
                value={newMindmapDescription}
                onChange={(e) => setNewMindmapDescription(e.target.value)}
                className="create-mindmap-textarea"
                rows={3}
              />
              <div className="create-form-actions">
                <button type="submit" className="create-submit-btn">
                  Create Mindmap
                </button>
                <button 
                  type="button" 
                  onClick={() => {
                    setShowCreateForm(false);
                    setNewMindmapTitle('');
                    setNewMindmapDescription('');
                  }}
                  className="create-cancel-btn"
                >
                  Cancel
                </button>
              </div>
            </div>
          </form>
        </div>
      )}

      <div className="mindmap-search">
        <input
          type="text"
          placeholder="Search mindmaps..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="mindmap-search-input"
        />
      </div>

      <div className="mindmap-content">
        {loading ? (
          <div className="mindmap-loading">
            <div className="loading-spinner"></div>
            <p>Loading mindmaps...</p>
          </div>
        ) : filteredMindmaps.length === 0 ? (
          <div className="mindmap-empty">
            {searchQuery ? (
              <div>
                <p>No mindmaps found matching "{searchQuery}"</p>
                <button onClick={() => setSearchQuery('')} className="clear-search-btn">
                  Clear search
                </button>
              </div>
            ) : (
              <div>
                <p>You don't have any mindmaps yet.</p>
                <button 
                  onClick={() => setShowCreateForm(true)}
                  className="create-first-mindmap-btn"
                >
                  Create your first mindmap
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="mindmap-list">
            {filteredMindmaps.map((mindmap) => (
              <div key={mindmap.id} className="mindmap-item" onClick={() => handleMindmapClick(mindmap.id)}>
                <div className="mindmap-item-header">
                  <h3 className="mindmap-item-title">{mindmap.title}</h3>
                  <button
                    className="mindmap-delete-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteMindmap(mindmap.id);
                    }}
                    title="Delete mindmap"
                  >
                    ×
                  </button>
                </div>
                {mindmap.description && (
                  <p className="mindmap-item-description">{mindmap.description}</p>
                )}
                <div className="mindmap-item-meta">
                  <span className="mindmap-item-id">/{mindmap.id}</span>
                  <span className="mindmap-item-date">
                    {formatDate(mindmap.updated_at)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Mindmap;

